/**
 * CJAS — service requests.  Jira CA-27.
 *
 * The four forms on cjas.no become one staff inbox. Every request gets a
 * reference number the person can quote on the phone, because "the one I
 * sent last Tuesday" is not a way to find anything.
 */

const forms = require('./forms');

/**
 * The service kinds.
 *
 * CORRECTED 2026-09-09 (mismatch 21). There were THREE vocabularies for the
 * same four forms:
 *
 *   forms.js      nikah · janazah · tour · fatiha · counselling · other
 *   requests.js   nikah · tour · fatiha · contact
 *   service_kind  nikah · tour · fatiha · contact
 *
 * The consequence was not cosmetic. **None of the app's service request forms
 * could be submitted.** A nikah was rejected for three missing required
 * fields that the app does not have and never rendered; a **janazah — the
 * shortest and most urgent form in the app, filled in by a family who have
 * just lost someone — came back `unknown_service`**, because there was no
 * enum value it could be stored under. CA-27 was marked done.
 *
 * `forms.js` is now the single source: it owns what is rendered, so it owns
 * the vocabulary. This list is derived from it rather than retyped beside it,
 * which is the only fix that stops the drift recurring — the same lesson as
 * dates.js.
 */
const KINDS = forms.KINDS.slice();

/**
 * `contact` and `other` are the same service under two names.
 *
 * cjas.no has always called its general enquiry form "kontakt"; the app calls
 * the same thing "other". Both are in `service_kind` so neither caller breaks,
 * but they are normalised to ONE value before storage — two buckets in the
 * staff inbox for one service is how a request goes unanswered because the
 * person watching the other bucket assumed somebody had it.
 */
const KIND_ALIASES = { contact: 'other' };

/** The canonical kind for storage and validation. */
function normaliseKind(kind) {
  const k = String(kind || '');
  return KIND_ALIASES[k] ?? k;
}
const STATUSES = ['new', 'in_progress', 'scheduled', 'closed', 'rejected'];
/**
 * The staff panel's words for each status (UX consistency audit U-01, 2026-10-03): kept here so the panel imports them
 * instead of declaring its own. The member app still says its own (app.requests.status.* — «Mottatt», «Avsluttet» …);
 * whether staff and members should share one vocabulary is an owner decision (U-D01), not taken here.
 */
const STATUS_NO = { new: 'Ny', in_progress: 'Under behandling', scheduled: 'Satt opp', closed: 'Lukket', rejected: 'Avvist' };

/** Which statuses may follow which. Prevents a closed request quietly reopening. */
const TRANSITIONS = {
  new:         ['in_progress', 'scheduled', 'closed', 'rejected'],
  in_progress: ['scheduled', 'closed', 'rejected'],
  scheduled:   ['in_progress', 'closed'],
  closed:      [],
  rejected:    [],
};

/**
 * Required fields per service, read off the rendered form.
 *
 * Not a second list. A form that renders eight fields and a validator that
 * requires three different ones is two specifications of one thing, and the
 * one nobody runs is the one that is wrong.
 */
function requiredFields(kind) {
  const schema = forms.schema(kind);
  if (!schema) return null;
  return schema.fields.filter(f => f.required).map(f => f.name);
}

/**
 * How a submitted form becomes a `service_request` row.
 *
 * The table has four named columns — `requester_name`, `email`, `phone`,
 * `requested_date` — plus a `fields` jsonb for everything else. The app posts
 * the form's own field names, which are richer on purpose: a nikah has two
 * parties, not one "requester", and flattening that at the form would lose
 * which name is whose.
 *
 * So the mapping lives here, once, and everything that is not mapped goes to
 * `fields` intact. Nothing is dropped.
 */
const COLUMN_SOURCES = {
  // Order matters. For a janazah both `contact_name` and `deceased_name` are
  // present, and the REQUESTER is the person who filled the form — putting
  // the deceased's name in that column is who staff would then ring.
  requester_name: ['requester_name', 'party_a_name', 'contact_name', 'name', 'deceased_name'],
  phone:          ['phone', 'contact_phone', 'telefon'],
  email:          ['email', 'contact_email', 'epost'],
  requested_date: ['requested_date', 'preferred_date', 'prayer_date', 'date'],
};

/**
 * The value for one form field, accepting any of its known aliases.
 *
 * The app posts `contact_phone`; the website's four forms have always posted
 * `phone`. Both are the same fact, and forcing one of the two callers to
 * reshape just moves the drift somewhere it is harder to see. A required
 * field is satisfied by ANY alias that maps to the same column.
 */
function valueFor(values = {}, fieldName) {
  if (values[fieldName] !== undefined && String(values[fieldName]).trim() !== '') {
    return values[fieldName];
  }
  for (const sources of Object.values(COLUMN_SOURCES)) {
    if (!sources.includes(fieldName)) continue;
    for (const alias of sources) {
      const v = values[alias];
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
    }
  }
  return undefined;
}

function toColumns(rawKind, values = {}) {
  const kind = normaliseKind(rawKind);
  const columns = {};
  const used = new Set();
  for (const [column, sources] of Object.entries(COLUMN_SOURCES)) {
    for (const src of sources) {
      const v = values[src];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        columns[column] = String(v).trim();
        used.add(src);
        break;
      }
    }
  }
  // Everything the columns did not take is kept whole. A "witnesses" field
  // silently vanishing from a nikah is a legal problem, not a data problem.
  const rest = {};
  for (const [k, v] of Object.entries(values)) if (!used.has(k)) rest[k] = v;
  return { columns, fields: rest };
}

/**
 * Documents that must be attached before a nikah can be scheduled.
 *
 * CORRECTED 2026-09-10: this said `['provingsattest', 'id_couple',
 * 'id_witnesses']` while forms.js — which renders the upload fields — asks
 * for `provingsattest`, `id_party_a`, `id_party_b`. So a nikah with every
 * document the app offered could never be scheduled. Read off the form now,
 * like KINDS (mismatch 21).
 */
const REQUIRED_DOCUMENTS = Object.fromEntries(forms.KINDS
  .map(k => [k, ((forms.schema(k) || {}).documents || []).filter(d => d.required).map(d => d.id)])
  .filter(([, docs]) => docs.length));

/** '2026-0184' — year plus a padded sequence. Readable over the phone. */
function makeReference(year, sequence) {
  return `${year}-${String(sequence).padStart(4, '0')}`;
}

/**
 * SI-04 (audit 2026-10-19): an address the inbox can put in a mailto: link as it is. The old check (`x@y.z`, no space)
 * took `a@x.no?cc=b@evil.no&body=…`, and «Send e-post» opened a mail with a hidden copy and a body. Letters (Norwegian
 * and other scripts too), digits and . _ + ' - before the @; a domain of letter/digit labels (IDN: blåbær.no) with a
 * top level of two letters or more; never ? & # % / = , ; : < > or a space. At most 254 characters.
 */
const EMAIL_RE = /^(?=.{3,254}$)[\p{L}\p{N}._+'-]+@(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?\.)+\p{L}{2,63}$/u;

function validate(rawKind, payload) {
  const kind = normaliseKind(rawKind);
  if (!KINDS.includes(kind)) return { ok: false, errors: [{ field: 'kind', reason: 'unknown_service' }] };
  const errors = [];
  const required = requiredFields(kind) || [];
  for (const f of required) {
    const v = valueFor(payload, f);
    if (v === undefined || v === null || String(v).trim() === '') {
      errors.push({ field: f, reason: 'required' });
    }
  }
  // Validate the MAPPED values, so `contact_email` from the app and `email`
  // from the website are checked by the same rule rather than one of them
  // slipping through unvalidated.
  const { columns } = toColumns(kind, payload);
  if (columns.email && !EMAIL_RE.test(columns.email)) {
    errors.push({ field: 'email', reason: 'invalid' });
  }
  if (columns.requested_date && !/^\d{4}-\d{2}-\d{2}$/.test(columns.requested_date)) {
    errors.push({ field: 'requested_date', reason: 'invalid_format' });
  }
  return { ok: errors.length === 0, errors };
}

function canTransition(from, to) {
  return (TRANSITIONS[from] || []).includes(to);
}

/**
 * Scheduling a nikah requires the documents. CJAS holds vigselsrett — this
 * is a legal obligation, not an internal preference, so it is a hard block.
 */
function canSchedule(request, attachedDocTypes = []) {
  const needed = REQUIRED_DOCUMENTS[request.kind] || [];
  const missing = needed.filter(d => !attachedDocTypes.includes(d));
  if (missing.length) return { ok: false, reason: 'missing_documents', missing };
  if (!canTransition(request.status, 'scheduled')) {
    return { ok: false, reason: 'invalid_transition', from: request.status };
  }
  return { ok: true };
}

/**
 * Retention date for an uploaded document, so nobody has to remember to
 * delete a prøvingsattest. Defaults come from the GDPR draft (CA-102).
 */
function retentionFor(kind, uploadedAt = new Date(), months = { nikah: 6, default: 24 }) {
  const d = new Date(uploadedAt);
  d.setMonth(d.getMonth() + (months[kind] ?? months.default));
  return d;
}

/** Open requests, oldest first, so nothing quietly ages out of sight. */
function inbox(requests, now = new Date(), staleDays = 5) {
  return (requests || [])
    .filter(r => r.status === 'new' || r.status === 'in_progress')
    .map(r => ({
      ...r,
      ageDays: Math.floor((now - new Date(r.created_at)) / 86400000),
      isStale: (now - new Date(r.created_at)) / 86400000 >= staleDays,
    }))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

module.exports = {
  KINDS, KIND_ALIASES, normaliseKind, STATUSES, STATUS_NO, TRANSITIONS, requiredFields, toColumns, valueFor, COLUMN_SOURCES, REQUIRED_DOCUMENTS,
  makeReference, validate, canTransition, canSchedule, retentionFor, inbox,
};
